// Percorso file: src/components/SystemView.jsx
//
// VISTE DI SISTEMA — le schermate che l'app mostra quando NON può
// aprire una home. Vivono qui, in un file solo, perché il gate
// (RequireRole) e l'instradatore (Home.jsx) mostrino esattamente le
// stesse pagine.
//
// Nessun rimbalzo muto: ogni vicolo cieco dice che cosa è successo e
// lascia sempre il Logout della barra utente (doc 07 §7.1).
//
// I testi sono in inglese, come il resto dell'app.
//
// ⚠️ ECCEZIONE DICHIARATA: `ArchivedView` non è più pura presentazione
// (cantiere «renew archiviato», settembre 2026). Porta il pulsante con cui
// l'archiviato CHIEDE il rinnovo per sé e per i figli di cui è tutore, in un
// gesto solo — vedi le avvertenze sopra il componente. Le altre due viste
// restano senza azioni.

import React, { useEffect, useState } from "react";
import { FaUserPlus } from "react-icons/fa";
import HomeShell from "./HomeShell";
import { roleLabel } from "../auth/access";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { annoDiLista } from "../utils/stagione";
// GLI ID DEI FIGLI COLLEGATI, dalla sede unica (utils/famiglia.js), col nome
// che questo file ha sempre usato. Gli id vanno al server e basta: è
// `POST /renew` che verifica, figlio per figlio, che `guardians` del minore
// elenchi chi chiama. Qui non si legge la scheda di nessun figlio e non si
// mostra alcun nome — scelta di sicurezza: un archiviato non apre la scheda
// di un minore, nemmeno del proprio.
import { idsDeiFigli as idsDeiFigliCollegati } from "../utils/famiglia";
import "./SystemView.css";

/** Pagina di sistema generica: titolo + corpo, dentro il telaio comune.
 *  `showSwitch` passa al telaio (default `true`, com'era): la sola
 *  `ArchivedView` lo spegne, vedi lì.
 *  `showLogout` è SEMPRE acceso: tutte e tre le viste sono vicoli ciechi
 *  (cantiere LOGOUT-1, vedi UserBar.jsx), e il Logout della barra è la
 *  loro uscita — le frasi «Use Logout at the top of the page» lo nominano. */
const SystemView = ({ title, showSwitch = true, children }) => (
  <HomeShell showSwitch={showSwitch} showLogout>
    <h1 className="qpsc-page-title">{title}</h1>
    {children}
  </HomeShell>
);

/**
 * Il messaggio del server, quando è scritto per essere letto (stesso
 * accorgimento di EnrolNextSeason.jsx: i 409 della rotta portano già un testo
 * in inglese rivolto al socio, e ricopiarlo qui farebbe due copie).
 */
const messaggioDelServer = (err, ripiego) => {
  try {
    const corpo = JSON.parse((err && err.body) || "");
    const testo = corpo && typeof corpo.error === "string" ? corpo.error.trim() : "";
    return testo || ripiego;
  } catch {
    return ripiego;
  }
};

// Il testo onesto per «non è nato nulla». Il pannello delle home dice «Nothing new to enrol
// for {anno}. If you think this is wrong, please contact the club.»; qui si usa un testo
// proprio perché l'archiviato che arriva a questo messaggio non ha chiesto nulla: non ha TITOLO (nessuna
// tessera ACTIVE/ARCHIVED di un anno precedente a quello d'iscrizione, in un
// ruolo rinnovabile — la regola è del server, ottobre 2026), oppure la rotta ha
// rifiutato la famiglia. In entrambi i casi la strada è il club.
const TESTO_NON_RINNOVATO =
  "We couldn't renew your membership automatically. Please contact the club.";

// LA RIGA DEI NON INCLUSI (R2.1), SENZA NOMI: qui nessuna scheda di figlio si
// legge (vedi l'import di `idsDeiFigliCollegati`), quindi si dice solo che
// qualcuno manca — non chi, non perché.
const TESTO_NON_INCLUSI = "Some members were not included. Please contact the club.";

/**
 * C'è almeno un FIGLIO fra i `skipped` di `POST /renew`? Si contano solo gli
 * id mandati come figli: chi chiama, se la rotta lo salta, non fa scattare
 * la riga. `skipped` è un elenco di `{ id }`; un id nudo si accetta lo stesso.
 */
const figliNonInclusi = (skipped, idsFigli) =>
  Array.isArray(skipped) &&
  skipped.some((voce) =>
    idsFigli.includes(typeof voce === "string" ? voce : voce && voce.id)
  );

/**
 * status ARCHIVED — l'account non è più utilizzabile, ma da settembre 2026
 * (decisione del committente) l'archiviato può CHIEDERE il rinnovo.
 *
 * IL SERVER DECIDE, QUESTA VISTA CHIEDE — come EnrolNextSeason.jsx, che è il
 * modello. Al clic parte UNA `POST /renew` con gli id dei figli collegati; la
 * rotta verifica il legame di tutela figlio per figlio, decide chi ha titolo,
 * fa nascere tessere PENDING dell'anno d'iscrizione e le lascia
 * all'approvazione dell'amministratore. Lo status resta ARCHIVED: questa
 * schermata è ancora l'unica che la persona vede, prima e dopo il clic.
 *
 * IN BLOCCO, SENZA NOMI: un solo pulsante, nessuna spunta, nessuna scheda di
 * minore aperta (scelta di sicurezza). Chi non ha figli rinnova solo sé.
 *
 * QUANDO COMPARE — le stesse due condizioni del pannello delle home, copiate
 * da lì: SOVRAPPOSIZIONE (`enrollmentSeason === operatingSeason + 1`) oppure
 * STAGIONE UNICA (`enrollmentSeason === operatingSeason`, ottobre 2026), e
 * ISCRIZIONI APERTE (`enrollmentsOpen !== false`). Fuori da queste la
 * schermata è identica a prima: il solo messaggio «Membership archived».
 * ⚠️ A stagione unica, all'Approve, lo specchio guarda l'anno in corso: la
 * persona torna ACTIVE subito (accettato dal committente). Qui la riga
 * «confirmed» del pannello delle home non esiste (lo stato è `giaChiesto`),
 * quindi la copia resta davvero una copia.
 *
 * ⚠️ DA DOVE VENGONO STAGIONE E PROFILO. All'ingresso AuthContext consegna
 * all'archiviato un `user` MINIMALE ({ firebaseUid, email, status }) e NON
 * legge la stagione. Al montaggio questa vista rilegge entrambi dalle sedi
 * uniche già esistenti — `refreshSeason` (GET /season-config) e
 * `refreshUserFromBackend` (GET /users?uid=, ramo «sé stessi», che il backend
 * serve intero anche a un archiviato) — senza aggiungere un ramo ad
 * AuthContext. Il `user` diventa così COMPLETO ma con `status: "ARCHIVED"`:
 * il gate (RequireRole, passo 3) continua a mostrare questa vista. Effetti di
 * riflesso dichiarati: la barra utente mostra il nome oltre all'email, e
 * l'hook delle notifiche (useFCMDeviceToken, `[user]`) rigira una volta.
 * Se una delle due riletture fallisce il pulsante NON compare (si chiude nel
 * verso sicuro) e la schermata resta quella di sempre.
 *
 * DOPO IL CLIC — lo stesso metro del pannello (YS-11.3): `renewedSeason` sul
 * documento, riletto con `refreshUserFromBackend`, confrontato con
 * `enrollmentSeason`; se coincidono il pulsante lascia il posto alla riga
 * «Renewal request received for <anno> — awaiting approval». Al login
 * successivo la riga c'è ancora, perché la rilettura al montaggio la ritrova.
 *
 * COSA NON FA: non elenca i figli, non dice quali siano stati rinnovati e
 * quali saltati (il conteggio sì; e se un figlio è saltato lo dice, senza
 * nome — R2.1), non dice se il club abbia approvato.
 */
export const ArchivedView = () => {
  const {
    user,
    operatingSeason,
    enrollmentSeason,
    enrollmentsOpen,
    refreshUserFromBackend,
    refreshSeason
  } = useAuth();

  // `pronto`: le due riletture del montaggio sono finite (bene o male).
  // `profiloLetto`: la scheda completa è arrivata davvero — senza di essa non
  // si sa se il rinnovo sia già stato chiesto, e un pulsante offerto a chi ha
  // già chiesto porterebbe a un messaggio falso.
  const [pronto, setPronto] = useState(false);
  const [profiloLetto, setProfiloLetto] = useState(false);
  const [invio, setInvio] = useState(false);
  const [esito, setEsito] = useState(null);

  // AL MONTAGGIO, UNA VOLTA: stagione e profilo completo. Le due funzioni
  // vengono da AuthContext e non sono memoizzate (cambiano identità a ogni
  // render), quindi la dipendenza è vuota di proposito — stessa scelta di
  // EnrolNextSeason.jsx per `chiaveFigli`.
  useEffect(() => {
    let annullato = false;

    (async () => {
      let scheda = null;

      try {
        const [, schedaLetta] = await Promise.all([
          refreshSeason ? refreshSeason() : Promise.resolve(null),
          refreshUserFromBackend ? refreshUserFromBackend() : Promise.resolve(null)
        ]);
        scheda = schedaLetta;
      } catch {
        // Nessun dettaglio nei log: si resta sulla schermata di sempre.
      }

      if (annullato) return;
      setProfiloLetto(Boolean(scheda));
      setPronto(true);
    })();

    return () => {
      annullato = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // LE STESSE DUE CONDIZIONI DEL PANNELLO (EnrolNextSeason.jsx), copiate.
  const sovrapposizione =
    Number.isInteger(operatingSeason) &&
    Number.isInteger(enrollmentSeason) &&
    enrollmentSeason === operatingSeason + 1;

  const stagioneUnica =
    Number.isInteger(operatingSeason) &&
    Number.isInteger(enrollmentSeason) &&
    enrollmentSeason === operatingSeason;

  const visibile =
    pronto &&
    profiloLetto &&
    (sovrapposizione || stagioneUnica) &&
    enrollmentsOpen !== false;

  // HA GIÀ CHIESTO PER QUEST'ANNO D'ISCRIZIONE? Stesso metro del pannello:
  // l'anno si CONFRONTA (un `renewedSeason` dell'anno scorso non conta), con
  // `annoDiLista` perché il campo arriva grezzo dallo spread del documento.
  const giaChiesto =
    Number.isInteger(enrollmentSeason) &&
    annoDiLista(user && user.renewedSeason) === enrollmentSeason;

  const rinnova = async () => {
    setInvio(true);
    setEsito(null);

    try {
      // ⚠️ ELENCO CHIUSO: dal corpo partono SOLO gli id dei figli collegati.
      // Non il proprio id (viene dal token), non i ruoli, non l'anno, non lo
      // status: che cosa nasca lo decide la rotta. Senza figli, elenco vuoto:
      // rinnova solo sé.
      const idsFigli = idsDeiFigliCollegati(user);
      const risposta = await api.post("/renew", {
        children: idsFigli
      });

      const nate = Array.isArray(risposta && risposta.renewed)
        ? risposta.renewed
        : [];
      const anno = (risposta && risposta.enrollmentSeason) || enrollmentSeason;

      // `renewed` vuoto è un 200 senza tessere: nessun titolo. Si dice il vero
      // e non lo stato «richiesta inviata».
      setEsito(
        nate.length > 0
          ? {
              tipo: "ok",
              testo: `Renewal for ${anno} requested for ${nate.length} ${
                nate.length === 1 ? "member" : "members"
              }. The club will review it and confirm.`,
              nonInclusi: figliNonInclusi(risposta && risposta.skipped, idsFigli)
                ? TESTO_NON_INCLUSI
                : ""
            }
          : { tipo: "vuoto", testo: TESTO_NON_RINNOVATO }
      );

      // IL PROFILO SI RILEGGE, SUBITO, e solo nel ramo riuscito (stessa regola
      // e stesse ragioni di EnrolNextSeason.jsx): è `renewedSeason` appena
      // scritto che fa comparire la riga di stato. Il suo guasto non è il
      // guasto del rinnovo: al peggio si resta sul profilo di prima.
      if (refreshUserFromBackend) {
        try {
          await refreshUserFromBackend();
        } catch {
          /* profilo stantìo: il rinnovo è comunque riuscito */
        }
      }
    } catch (err) {
      const stato = err && err.status;

      let testo;
      if (stato === 409) {
        // Iscrizioni chiuse, oppure stagione illeggibile: lo dice il server.
        testo = messaggioDelServer(
          err,
          "Renewal is not possible right now. Please try again in a moment."
        );
      } else if (stato === 403 || stato === 400) {
        // 403: la rotta ha rifiutato la famiglia IN BLOCCO — un id fra i
        // collegati non porta il legame di ritorno (`guardians`), o non c'è
        // più. Nulla è nato, nemmeno per sé: il legame è il metro e non si
        // aggira da qui. La strada è il club, che i legami li sistema.
        testo = TESTO_NON_RINNOVATO;
      } else if (!stato) {
        testo =
          "Could not reach the club. Please check your connection and try again.";
      } else {
        testo = "Something went wrong. Please try again in a moment.";
      }

      setEsito({ tipo: "errore", testo });
    } finally {
      setInvio(false);
    }
  };

  // Dopo un 200 (nato o no) il pulsante non si ripropone: un secondo clic
  // darebbe la stessa risposta. Dopo un errore resta, per riprovare.
  const concluso = Boolean(esito) && esito.tipo !== "errore";

  // ⚠️ `showSwitch={false}`, e serve DA QUESTA fetta: con l'oggetto minimale
  // `getActiveRoles(user)` era vuoto e la voce «Switch role» della banda non
  // compariva mai; col profilo completo un archiviato che conservi tessere
  // ACTIVE di ALTRE annate (la cascata tocca solo l'anno bersaglio) potrebbe
  // contarne due e vedersi offrire un cambio di ruolo che porta di nuovo qui
  // (il gate precede tutto). Spenta, la schermata resta quella di sempre.
  return (
    <SystemView title="Membership archived" showSwitch={false}>
      <p className="qpsc-body-text">
        Your membership is archived. For any questions, please contact the club at{" "}
        <a className="qpsc-mail-link" href="mailto:footballdepartment@queensparksc.com.au">
          footballdepartment@queensparksc.com.au
        </a>
        .
      </p>

      {visibile && giaChiesto && (
        <>
          {/* LO STATO al posto del pulsante (YS-11.3): testo, non un pulsante
              spento. Sotto, per la sola sessione del clic, il riscontro con
              il conteggio. */}
          <p className="qpsc-sysview__state">
            Renewal request received for {enrollmentSeason} &mdash; awaiting
            approval
          </p>
          {esito && esito.tipo === "ok" && (
            <p className="qpsc-sysview__feedback qpsc-sysview__feedback--ok">
              {esito.testo}
            </p>
          )}
          {esito && esito.nonInclusi && (
            <p className="qpsc-sysview__feedback">{esito.nonInclusi}</p>
          )}
        </>
      )}

      {visibile && !giaChiesto && (
        <>
          {!concluso && (
            <>
              <p className="qpsc-body-text">
                You can ask to renew your membership for {enrollmentSeason}. If
                you are the guardian of other members, their renewal is
                requested together with yours. Renewals start as requests: the
                club reviews and confirms them.
              </p>
              {/* Il pulsante oro normale dell'app (doc 07 §5.1), nel suo
                  contenitore: è un'azione nella pagina, non una voce di
                  navigazione. */}
              <div className="qpsc-menu">
                <button
                  type="button"
                  className="qpsc-btn-gold"
                  onClick={rinnova}
                  disabled={invio}
                >
                  <span className="qpsc-btn-gold__icon">
                    <FaUserPlus />
                  </span>
                  {invio ? "Sending…" : `Renew membership for ${enrollmentSeason}`}
                </button>
              </div>
            </>
          )}

          {esito && (
            <p
              className={`qpsc-sysview__feedback${
                esito.tipo === "ok"
                  ? " qpsc-sysview__feedback--ok"
                  : " qpsc-sysview__feedback--error"
              }`}
            >
              {esito.testo}
            </p>
          )}
          {esito && esito.nonInclusi && (
            <p className="qpsc-sysview__feedback">{esito.nonInclusi}</p>
          )}
        </>
      )}
    </SystemView>
  );
};

/**
 * Il ruolo richiesto dalla pagina non è fra quelli posseduti.
 *
 * ⚠️ Per chi è SOLO GUEST (P3, 24/9/2026) la prima riga cambia: il ruolo
 * richiesto è quello dell'elenco dei soci (RequireRole, passo 6) e «reserved
 * to the Admin role» era impreciso. Il predicato è quello di RequireRole —
 * ruoli posseduti non vuoti e tutti GUEST — letto su `heldRoles`, che
 * RequireRole passa già come `getActiveRoles(user)`. Per ogni altro ruolo il
 * testo è quello di prima.
 */
export const AccessDeniedView = ({ requiredRole, heldRoles = [] }) => (
  <SystemView title="Access not allowed">
    <p className="qpsc-body-text">
      {heldRoles.length > 0 && heldRoles.every((r) => r === "GUEST") ? (
        "This page is for club members. Your clinic area is on your home page."
      ) : (
        <>This page is reserved to the {roleLabel(requiredRole)} role.</>
      )}
    </p>
    <p className="qpsc-note">
      {heldRoles.length > 0
        ? `Your account holds: ${heldRoles.map(roleLabel).join(", ")}.`
        : "Your account holds no role yet."}
    </p>
    <p className="qpsc-note">
      Use Logout at the top of the page to sign in with another account.
    </p>
  </SystemView>
);

/** Nessun ruolo, o un ruolo per cui non esiste una home. */
export const UnknownRoleView = ({ roles = [] }) => (
  <SystemView title="Role not recognised">
    <p className="qpsc-body-text">
      {roles.length > 0
        ? "This app has no home for the role on your account."
        : "There is no role on your account."}
    </p>
    <p className="qpsc-note">
      {roles.length > 0
        ? `Role found: ${roles.join(", ")}.`
        : "A club administrator has to assign your role."}
    </p>
    <p className="qpsc-note">
      Use Logout at the top of the page to sign in with another account.
    </p>
  </SystemView>
);

export default SystemView;
